import {Box, Card, IconButton, Typography} from '@mui/material'
import {FC, ReactNode, useEffect, useMemo} from 'react'
import {
  AutocompleteInput,
  maxLength,
  number,
  regex,
  required,
  SelectInput,
  TextInput,
  useTranslate,
} from 'react-admin'
import {useFormContext, useWatch} from 'react-hook-form'
import {makeStyles} from 'tss-react/mui'

import {DraggableList} from '../../components/DraggableList'
import {CloseIcon, GripVerticalIcon} from '../../components/icons'
import RichTranslationText from '../../components/RichTranslationText'
import SubtitledInput from '../../components/SubtitledInput'
import {
  ChatMessageTemplateButton,
  ChatMessageTemplateButtonTypesEnum,
} from '../../types/graphqlSchema'
import {EMOJI_REGEX} from '../../utils/consts'
import {COUNTRY_CODES} from '../../utils/phoneNumber'

const TemplateBuilderButtons = () => {
  const {classes: styles} = useStyles({})
  const buttonTypeDefaultValues = useButtonTypeDefaultValues()
  const {
    chatMessageTemplateButtons = [],
  } = useWatch<{chatMessageTemplateButtons: ChatMessageTemplateButton[]}>()
  const {setValue} = useFormContext()
  const addButton = (buttonType: string) => {
    // We set to undefined so the same select input item can be selected multiple times
    // consecutively.
    setValue('buttonType', undefined)
    const newButtons = [
      ...chatMessageTemplateButtons,
      {type: buttonType, ...buttonTypeDefaultValues[buttonType]},
    ]
    const groupedButtons = [
      ...newButtons.filter(
        b => b.type !== ChatMessageTemplateButtonTypesEnum.QuickReply
      ),
      ...newButtons.filter(
        b => b.type === ChatMessageTemplateButtonTypesEnum.QuickReply
      ),
    ]
    setValue('chatMessageTemplateButtons', groupedButtons)
  }
  const translate = useTranslate()
  return (
    <div>
      <SubtitledInput
        isEmptyValidationErrorMessageHidden
        subtitle={translate(
          'resources.chat_message_templates.create.inputs.buttons.subtitle'
        )}
        title={translate('resources.chat_message_templates.create.inputs.buttons.title')}
      >
        <SelectInput
          choices={
            Object.keys(BUTTON_TYPE_TO_COMPONENT)
              .filter(buttonType =>
                BUTTON_TYPE_TO_COMPONENT[buttonType].canBeAdded(
                  chatMessageTemplateButtons.map(b => b.type)
                )
              )
              .map((type, index) => ({
                id: index,
                key: type,
                name: translate(
                  `resources.chat_message_templates.create.buttonTypes.${type}`
                ),
              }))
          }
          className={styles.buttonTypeSelectInput}
          emptyText={translate(
            'resources.chat_message_templates.create.inputs.buttons.buttonType.emptyText'
          )}
          label={useTranslate()(
            'resources.chat_message_templates.create.inputs.buttons.buttonType.label'
          )}
          name="buttonType"
          onChange={e => addButton(e.target.value)}
          optionValue="key"
          readOnly={chatMessageTemplateButtons.length === MAX_BUTTONS_COUNT}
          source="buttonType"
        />
      </SubtitledInput>
      {chatMessageTemplateButtons.some(b => b.type !== 'QUICK_REPLY') && (
        <Card className={styles.draggableListCard}>
          <DraggableList
            items={chatMessageTemplateButtons}
            onDisplayItem={(item, i) => {
              if (item.type === 'QUICK_REPLY') return null
              const ButtonComponent = BUTTON_TYPE_TO_COMPONENT[item.type].component
              return <ButtonComponent index={i} />
            }}
            onItemsSwapped={items => setValue('chatMessageTemplateButtons', items)}
          />
        </Card>
      )}
      {(
        chatMessageTemplateButtons.some(b => b.type === 'QUICK_REPLY') &&
        chatMessageTemplateButtons.some(b => b.type !== 'QUICK_REPLY')
      ) && <br/>}
      {chatMessageTemplateButtons.some(b => b.type === 'QUICK_REPLY') && (
        <Card className={styles.draggableListCard}>
          <DraggableList
            items={chatMessageTemplateButtons}
            onDisplayItem={(item, i) => {
              if (item.type !== 'QUICK_REPLY') return null
              const ButtonComponent = BUTTON_TYPE_TO_COMPONENT[item.type].component
              return <ButtonComponent index={i} />
            }}
            onItemsSwapped={items => setValue('chatMessageTemplateButtons', items)}
          />
        </Card>
      )}
    </div>
  )
}

const QuickReplyButtonInputs = ({index, ...props}) => {
  const buttons: ChatMessageTemplateButton[] = useWatch({
    name: 'chatMessageTemplateButtons',
  })
  const translate = useTranslate()
  const {classes: styles} = useStyles({})
  return (
    <ButtonInputsContainer index={index} {...props}>
      <Box className={styles.buttonInputsGroup} width="100%">
        <SubtitledInput
          title={translate(
            'resources.chat_message_templates.create.inputs.buttons.buttonTextTitle'
          )}
        >
          <TextInput
            source={`chatMessageTemplateButtons.${index}.quickReply`}
            label=""
            placeholder={translate(
              'resources.chat_message_templates.create.inputs.buttons.quickReply.label'
            )}
            validate={[
              required(),
              v => buttons.some((b, i) =>
                (i !== index) &&
                (b.type === ChatMessageTemplateButtonTypesEnum.QuickReply) &&
                (b.quickReply?.trim() === v.trim())
              ) ? translate(
                  'resources.chat_message_templates.create.inputs.buttons.quickReply.' +
                'validations.duplication'
                ) : null,
              maxLength(25),
            ]}
          />
        </SubtitledInput>
      </Box>
    </ButtonInputsContainer>
  )
}

const CopyCodeButtonInputs = ({index, ...props}) => {
  const {classes: styles} = useStyles({})
  return (
    <ButtonInputsContainer index={index} {...props}>
      <Box className={styles.buttonInputsGroup} width="100%" gap="8px">
        <SubtitledInput
          title={useTranslate()(
            'resources.chat_message_templates.create.inputs.buttons.buttonTextTitle'
          )}
        >
          <TextInput
            source=""
            // Should not translate
            label="Copy offer code"
            readOnly
          />
        </SubtitledInput>
        <SubtitledInput
          title={useTranslate()(
            'resources.chat_message_templates.create.inputs.buttons.copyCodeExample.title'
          )}
        >
          <TextInput
            source={`chatMessageTemplateButtons.${index}.copyCodeExample`}
            label=""
            placeholder={useTranslate()(
              'resources.chat_message_templates.create.inputs.buttons' +
              '.copyCodeExample.label'
            )}
            validate={[
              required(),
              maxLength(15),
              regex(/^[a-zA-Z0-9]+$/, useTranslate()('validations.alphanumeric')),
            ]}
          />
        </SubtitledInput>
      </Box>
    </ButtonInputsContainer>
  )
}

const PhoneNumberButtonInputs = ({index, ...props}) => {
  const {classes: styles} = useStyles({})
  return (
    <ButtonInputsContainer index={index} {...props}>
      <Box className={styles.phoneNumberButtonInputsRoot}>
        <SubtitledInput
          title={useTranslate()(
            'resources.chat_message_templates.create.inputs.buttons.buttonTextTitle'
          )}
        >
          <TextInput
            label=""
            placeholder={useTranslate()(
              'resources.chat_message_templates.create.inputs.buttons.phoneNumber.' +
              'text.label',
            )}
            source={`chatMessageTemplateButtons.${index}.phoneNumberText`}
            validate={[
              required(),
              maxLength(MAX_BUTTON_TITLE_LENGTH),
              regex(EMOJI_REGEX, useTranslate()('validations.emojisNotAllowed')),
            ]}
          />
        </SubtitledInput>
        <SubtitledInput
          isEmptyValidationErrorMessageHidden
          title={useTranslate()(
            'resources.chat_message_templates.create.inputs.buttons.phoneNumber.' +
            'countryCode.label',
          )}
        >
          <AutocompleteInput
            className={styles.countryCodeAutoCompleteInput}
            choices={COUNTRY_CODES.map(({code, dial_code, name}, i) => ({
              countryName: name, key: `${dial_code}|${i}`, name: `${code} ${dial_code}`,
            }))}
            defaultValue="+49"
            format={v => {
              // Find the matching key to re-select the correct option
              const index = COUNTRY_CODES.findIndex(({dial_code}) => dial_code === v)
              return index !== -1 ? `${v}|${index}` : ''
            }}
            label=""
            matchSuggestion={(filter, suggestion) =>
              suggestion.name.toLowerCase().includes(filter.toLowerCase()) ||
              suggestion.countryName.toLowerCase().includes(filter.toLowerCase())
            }
            // This is a workaround to prevent the React error
            // "Encountered two children with the same key" as the dial_code is
            // not unique. We use a combination of "parse()" and "format()".
            // "parse()" extracts just the dial_code to store, while "format()"
            // re-selects the correct option.
            parse={v => v?.split('|')[0]}
            optionValue="key"
            source={`chatMessageTemplateButtons.${index}.phoneNumberCountryCode`}
            validate={required()}
            variant="outlined"
          />
        </SubtitledInput>
        <SubtitledInput
          title={useTranslate()(
            'resources.chat_message_templates.create.inputs.buttons.phoneNumber.' +
            'phoneNumber.label',
          )}
        >
          <TextInput
            source={`chatMessageTemplateButtons.${index}.phoneNumber`}
            type="tel"
            placeholder="123"
            label=""
            validate={[required(), number(), maxLength(20)]}
          />
        </SubtitledInput>
      </Box>
    </ButtonInputsContainer>
  )
}

const UrlButtonInputs = ({index, ...props}) => {
  const {classes: styles} = useStyles({})
  const translate = useTranslate()
  const buttons = useWatch({
    defaultValue: [],
    name: 'chatMessageTemplateButtons',
  })
  const {setValue} = useFormContext()
  const urlType = buttons[index]?.urlType
  useEffect(() => {
    if (urlType !== 'DYNAMIC' && buttons[index]?.urlParameterExample) {
      setValue(`chatMessageTemplateButtons.${index}.urlParameterExample`, undefined)
    }
  }, [urlType, setValue, buttons, index])
  return (
    <ButtonInputsContainer index={index} {...props}>
      <Box className={styles.websiteUrlButtonInputsRoot}>
        <SubtitledInput
          title={useTranslate()(
            'resources.chat_message_templates.create.inputs.buttons.url.' +
            'text.label',
          )}
        >
          <TextInput
            source={`chatMessageTemplateButtons.${index}.urlText`}
            label=""
            placeholder={useTranslate()(
              'resources.chat_message_templates.create.inputs.buttons.url.' +
              'text.label',
            )}
            style={{width: '100%'}}
            validate={[
              required(),
              maxLength(MAX_BUTTON_TITLE_LENGTH),
              regex(EMOJI_REGEX, useTranslate()('validations.emojisNotAllowed')),
            ]}
          />
        </SubtitledInput>
        <SubtitledInput
          title={useTranslate()(
            'resources.chat_message_templates.create.inputs.buttons.url.' +
            'type.label',
          )}
        >
          <SelectInput
            variant="outlined"
            source={`chatMessageTemplateButtons.${index}.urlType`}
            label=""
            choices={[
              {
                key: 'STATIC',
                name: useTranslate()(
                  'resources.chat_message_templates.create.buttonURLTypes.STATIC'
                ),
              },
              {
                key: 'DYNAMIC',
                name: useTranslate()(
                  'resources.chat_message_templates.create.buttonURLTypes.DYNAMIC'
                ),
              },
            ]}
            defaultValue="STATIC"
            optionValue="key"
            validate={required()}
          />
        </SubtitledInput>
        <Box display="flex" alignItems="center" gap="4px">
          <SubtitledInput
            title={useTranslate()(
              'resources.chat_message_templates.create.inputs.buttons.url.' +
              'url.label',
            )}
          >
            <TextInput
              source={`chatMessageTemplateButtons.${index}.url`}
              placeholder="https://example.com"
              label=""
              validate={[
                required(),
                v => v.startsWith('https://') ?
                  null : translate('validations.websiteHttpsUrl'),
                v => URL.canParse(v) ? null : translate('validations.websiteUrl'),
                maxLength(2000),
              ]}
            />
          </SubtitledInput>
          {urlType === 'DYNAMIC' && (
            <Typography
              style={{alignSelf: 'start', marginTop: '3.5rem'}}
              color="textDisabled"
            >
              {`{{1}}`}
            </Typography>
          )}
        </Box>
      </Box>
      {urlType === 'DYNAMIC' && (
        <Box>
          <Typography variant="subtitle2">
            {translate(
              'resources.chat_message_templates.create.inputs.buttons.url.' +
              'parameterExample.title'
            )}
          </Typography>
          {translate(
            'resources.chat_message_templates.create.inputs.buttons.url.' +
            'parameterExample.tip'
          )}
          <Box>
            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="subtitle2" paragraph>{`{{1}}`}</Typography>
              <TextInput
                label={
                  translate(
                    'resources.chat_message_templates.create.inputs.buttons.url.' +
                    'parameterExample.label',
                    {buttonUrl: `${buttons[index].url || ''}{{1}}`}
                  )
                }
                source={`chatMessageTemplateButtons.${index}.urlParameterExample`}
                style={{width: '100%'}}
                validate={[required(), maxLength(2000)]}
              />
            </Box>
            {buttons[index].urlParameterExample && (
              <Box display="flex" alignItems="center">
                <RichTranslationText
                  rootElement={Typography}
                  variant="body2"
                  translationKey={
                    'resources.chat_message_templates.create.inputs.buttons.url.' +
                    'parameterExample.subLabel'
                  }
                  translationOptions={{
                    buttonUrl: (buttons[index].url || '') +
                      buttons[index].urlParameterExample,
                  }}
                  tags={{url: match => (
                    <Typography
                      className={styles.buttonUrlPreview}
                      color="textSecondary"
                      component="span"
                      sx={{verticalAlign: 'top'}}
                      variant="body2"
                    >
                      {match}
                    </Typography>
                  )}}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </ButtonInputsContainer>
  )
}

const ButtonInputsContainer: FC<ButtonInputsContainerProps> = ({
  children, className, index,
}) => {
  const {
    chatMessageTemplateButtons: buttons = [],
  } = useWatch<{chatMessageTemplateButtons: ChatMessageTemplateButton[]}>()
  const {classes: styles} = useStyles({})
  const {setValue} = useFormContext()
  const removeButton = (index: number) => {
    setValue('chatMessageTemplateButtons', buttons.filter((_, i) => i !== index))
  }
  const translate = useTranslate()
  return (
    <div className={styles.buttonInputsContainer}>
      <Typography variant="subtitle2" className={styles.buttonInputsContainerTitle}>
        {translate(
          `resources.chat_message_templates.create.buttonTypes.${buttons[index].type}`
        )}
      </Typography>
      <Box className={`${styles.buttonInputsContainerButtonsBox} ${className ?? ''}`}>
        {buttons.length > 1 && <GripVerticalIcon />}
        <Box width="100%">{children}</Box>
        <IconButton onClick={() => removeButton(index)}>
          <CloseIcon size="small" />
        </IconButton>
      </Box>
    </div>
  )
}

const useButtonTypeDefaultValues = () => {
  const translate = useTranslate()
  return useMemo<Record<
    ChatMessageTemplateButtonTypesEnum,
    Partial<MakeNullable<ChatMessageTemplateButton>>
  >>(() => ({
    [ChatMessageTemplateButtonTypesEnum.CopyCode]: {
      copyCodeExample: null,
    },
    [ChatMessageTemplateButtonTypesEnum.PhoneNumber]: {
      phoneNumber: null,
      phoneNumberCountryCode: null,
      phoneNumberText: translate(
        'resources.chat_message_templates.create.buttonTypes.PHONE_NUMBER'
      ),
    },
    [ChatMessageTemplateButtonTypesEnum.QuickReply]: {
      quickReply: null,
    },
    [ChatMessageTemplateButtonTypesEnum.Url]: {
      url: null,
      urlParameterExample: null,
      urlText: translate('resources.chat_message_templates.create.buttonTypes.URL'),
      urlType: null,
    },
  }), [translate])
}

const BUTTON_TYPE_TO_COMPONENT = {
  [ChatMessageTemplateButtonTypesEnum.CopyCode]: {
    canBeAdded: (buttonTypes: ChatMessageTemplateButtonTypesEnum[]) =>
      buttonTypes.length < MAX_BUTTONS_COUNT &&
      buttonTypes.filter(
        b => b === ChatMessageTemplateButtonTypesEnum.CopyCode
      ).length < 1,
    component: CopyCodeButtonInputs,
  },
  [ChatMessageTemplateButtonTypesEnum.PhoneNumber]: {
    canBeAdded: (buttonTypes: ChatMessageTemplateButtonTypesEnum[]) =>
      buttonTypes.length < MAX_BUTTONS_COUNT &&
      buttonTypes.filter(
        b => b === ChatMessageTemplateButtonTypesEnum.PhoneNumber
      ).length < 1,
    component: PhoneNumberButtonInputs,
  },
  [ChatMessageTemplateButtonTypesEnum.QuickReply]: {
    canBeAdded: (buttonTypes: ChatMessageTemplateButtonTypesEnum[]) =>
      buttonTypes.length < MAX_BUTTONS_COUNT,
    component: QuickReplyButtonInputs,
  },
  [ChatMessageTemplateButtonTypesEnum.Url]: {
    canBeAdded: (buttonTypes: ChatMessageTemplateButtonTypesEnum[]) =>
      buttonTypes.length < MAX_BUTTONS_COUNT &&
      buttonTypes.filter(b => b === ChatMessageTemplateButtonTypesEnum.Url).length < 2,
    component: UrlButtonInputs,
  },
}

const MAX_BUTTONS_COUNT = 10
const MAX_BUTTON_TITLE_LENGTH = 25
const useStyles = makeStyles<any>()(theme => {
  const threeButtonFieldStyles = {
    display: 'grid',
    gap: theme.remSpacing(1),
    gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr) minmax(0, 3fr)',
    [theme.breakpoints.down('lg')]: {
      '& > :nth-child(3)': {
        gridColumn: '1 / -1',
      },
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gridTemplateRows: 'auto auto',
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  }
  return {
    buttonInputsContainer: {
      margin: `${theme.remSpacing(3)} ${theme.remSpacing(1)} ${theme.remSpacing(1)}`,
    },
    buttonInputsContainerButtonsBox: {
      '& .ra-input': {
        minWidth: 0,
      },
      alignItems: 'center',
      display: 'flex',
      gap: theme.remSpacing(2),
    },
    buttonInputsContainerTitle: {
      marginBottom: theme.remSpacing(3),
    },
    buttonInputsGroup: {
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
      },
      display: 'flex',
    },
    buttonTypeSelectInput: {
      '& .MuiInputBase-root': {
        minHeight: 'auto',
      },
      '& .MuiSelect-select': {
        height: theme.remSpacing(7.5),
      },
    },
    buttonUrlPreview: {
      [theme.breakpoints.down('lg')]: {
        width: '60vw',
      },
      display: 'inline-block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '27vw',
    },
    countryCodeAutoCompleteInput: {
      '& .MuiOutlinedInput-root': {
        paddingRight: '0 !important',
      },
      '& .RaAutocompleteInput-textField': {
        minWidth: 0,
      },
    },
    draggableListCard: {
      '& .DraggableListItem': {
        '&:last-child': {
          borderBottom: 'none',
        },
        borderBottom: `1px solid ${theme.palette.disabled.main}`,
      },
      border: `1px solid ${theme.palette.disabled.main}`,
      padding: `0 ${theme.remSpacing(1)}`,
    },
    phoneNumberAndCodeInputsWrapper: {
      display: 'flex',
    },
    phoneNumberButtonInputsRoot: {
      ...threeButtonFieldStyles,
    },
    websiteUrlButtonInputsRoot: {
      ...threeButtonFieldStyles,
    },
  }
})

interface ButtonInputsContainerProps {
  className?: string
  children: ReactNode
  index: number
}

type MakeNullable<T> = {[P in keyof T]: T[P] | null}

export default TemplateBuilderButtons
